import { TagBadge } from './TagsComponent';
import type { Tag, TagCondition, TagRule } from '@/data/logs';
import { $tags } from '@/stores';
import { closeDialog } from '@/stores/dialog';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { useState } from 'react';

const stringOperators = [
  ['equals', 'Equals'],
  ['contains', 'Contains'],
  ['startsWith', 'Starts with'],
  ['endsWith', 'Ends with'],
  ['regex', 'Regular expression'],
] as const;
type StringOperator = (typeof stringOperators)[number][0];

function createCondition(type: TagCondition['type']): TagCondition {
  if (type === 'callSign') {
    return {
      type,
      from: '',
      fromOperator: 'contains',
      fromCaseSensitive: false,
      to: '',
      toOperator: 'contains',
      toCaseSensitive: false,
    };
  }

  return {
    type,
    value: '',
    operator: 'contains',
    caseSensitive: false,
  };
}

function createRule(): TagRule {
  return {
    enabled: true,
    priority: 0,
    logic: 'AND',
    conditions: [createCondition('description')],
  };
}

export function TagsEditor() {
  const tags = useStore($tags);
  const [editedTags, setEditedTags] = useState<Tag[]>(tags);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#ff0000');

  const handleSave = () => {
    $tags.set(editedTags);

    closeDialog();
  };

  const handleUpdateTag = (tagId: string, updates: Partial<Tag>) => {
    setEditedTags(prev =>
      prev.map(tag => (tag.id === tagId ? { ...tag, ...updates } : tag)),
    );
  };

  const handleDeleteTag = (tagId: string) => {
    setEditedTags(prev => prev.filter(tag => tag.id !== tagId));
  };

  const handleUpdateRule = (
    tagId: string,
    ruleIndex: number,
    updates: Partial<TagRule>,
  ) => {
    setEditedTags(prev =>
      prev.map(tag =>
        tag.id === tagId
          ? {
              ...tag,
              rules: (tag.rules || []).map((rule, index) =>
                index === ruleIndex ? { ...rule, ...updates } : rule,
              ),
            }
          : tag,
      ),
    );
  };

  const handleUpdateCondition = (
    tagId: string,
    ruleIndex: number,
    conditionIndex: number,
    updates: Partial<TagCondition>,
  ) => {
    setEditedTags(prev =>
      prev.map(tag =>
        tag.id === tagId
          ? {
              ...tag,
              rules: (tag.rules || []).map((rule, currentRuleIndex) =>
                currentRuleIndex === ruleIndex
                  ? {
                      ...rule,
                      conditions: rule.conditions.map(
                        (condition, currentConditionIndex) =>
                          currentConditionIndex === conditionIndex
                            ? ({ ...condition, ...updates } as TagCondition)
                            : condition,
                      ),
                    }
                  : rule,
              ),
            }
          : tag,
      ),
    );
  };

  const handleAddRule = (tagId: string) => {
    setEditedTags(prev =>
      prev.map(tag =>
        tag.id === tagId
          ? { ...tag, rules: [...(tag.rules || []), createRule()] }
          : tag,
      ),
    );
  };

  const handleDeleteRule = (tagId: string, ruleIndex: number) => {
    setEditedTags(prev =>
      prev.map(tag =>
        tag.id === tagId
          ? {
              ...tag,
              rules: (tag.rules || []).filter(
                (_, currentRuleIndex) => currentRuleIndex !== ruleIndex,
              ),
            }
          : tag,
      ),
    );
  };

  const handleAddCondition = (
    tagId: string,
    ruleIndex: number,
    type: TagCondition['type'],
  ) => {
    setEditedTags(prev =>
      prev.map(tag =>
        tag.id === tagId
          ? {
              ...tag,
              rules: (tag.rules || []).map((rule, currentRuleIndex) =>
                currentRuleIndex === ruleIndex
                  ? {
                      ...rule,
                      conditions: [...rule.conditions, createCondition(type)],
                    }
                  : rule,
              ),
            }
          : tag,
      ),
    );
  };

  const handleDeleteCondition = (
    tagId: string,
    ruleIndex: number,
    conditionIndex: number,
  ) => {
    setEditedTags(prev =>
      prev.map(tag =>
        tag.id === tagId
          ? {
              ...tag,
              rules: (tag.rules || []).map((rule, currentRuleIndex) =>
                currentRuleIndex === ruleIndex
                  ? {
                      ...rule,
                      conditions: rule.conditions.filter(
                        (_, currentConditionIndex) =>
                          currentConditionIndex !== conditionIndex,
                      ),
                    }
                  : rule,
              ),
            }
          : tag,
      ),
    );
  };

  const handleAddTag = () => {
    if (!newTagName.trim()) return;

    const newTag: Tag = {
      id: `tag-${Date.now()}`,
      name: newTagName.trim(),
      color: newTagColor,
    };

    setEditedTags(prev => [...prev, newTag]);
    setNewTagName('');
    setNewTagColor('#ff0000');
  };

  return (
    <div
      className="stop-scrolling fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={closeDialog}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-2xl font-semibold text-gray-900">Edit Tags</h2>
          <p className="mt-1 text-sm text-gray-500">
            Create and customize tags for organizing your logs
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Existing Tags */}
          <div className="space-y-4">
            {editedTags.map(tag => (
              <div
                key={tag.id}
                className="rounded-lg border border-gray-300 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {tag.name}
                  </h3>
                  <button
                    onClick={() => handleDeleteTag(tag.id)}
                    className="cursor-pointer rounded px-2 py-1 text-sm font-medium text-red-600 transition-colors hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor={`tag-name-${tag.id}`}
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      Tag Name
                    </label>
                    <input
                      id={`tag-name-${tag.id}`}
                      type="text"
                      value={tag.name}
                      onChange={e =>
                        handleUpdateTag(tag.id, { name: e.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div className="flex flex-row justify-end gap-3">
                    <div>
                      <label
                        htmlFor={`tag-color-${tag.id}`}
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Color
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          id={`tag-color-${tag.id}`}
                          type="color"
                          value={tag.color}
                          onChange={e =>
                            handleUpdateTag(tag.id, { color: e.target.value })
                          }
                          className="h-10 w-12 cursor-pointer rounded border border-gray-300 p-0"
                        />
                        <input
                          type="text"
                          value={tag.color}
                          onChange={e =>
                            handleUpdateTag(tag.id, { color: e.target.value })
                          }
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor={`tag-bg-${tag.id}`}
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Background Color
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          id={`tag-bg-${tag.id}`}
                          type="color"
                          value={tag.bgColor || '#ffffff'}
                          onChange={e =>
                            handleUpdateTag(tag.id, {
                              bgColor: e.target.value,
                            })
                          }
                          className="h-10 w-12 cursor-pointer rounded border border-gray-300 p-0"
                        />
                        <input
                          type="text"
                          value={tag.bgColor || ''}
                          onChange={e =>
                            handleUpdateTag(tag.id, {
                              bgColor: e.target.value || undefined,
                            })
                          }
                          placeholder="Optional"
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Preview */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Preview
                    </label>
                    <TagBadge tag={tag} />
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Conditions
                      </h4>
                      <button
                        type="button"
                        onClick={() => handleAddRule(tag.id)}
                        className="cursor-pointer rounded px-2 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50"
                      >
                        Add Rule
                      </button>
                    </div>
                    <div className="space-y-3">
                      {(tag.rules || []).map((rule, ruleIndex) => (
                        <div
                          key={`${tag.id}-rule-${ruleIndex}`}
                          className="rounded-md border border-gray-200 bg-gray-50 p-3"
                        >
                          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={rule.enabled}
                                  onChange={e =>
                                    handleUpdateRule(tag.id, ruleIndex, {
                                      enabled: e.target.checked,
                                    })
                                  }
                                />
                                Enabled
                              </label>
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                Match
                                <select
                                  value={rule.logic}
                                  onChange={e =>
                                    handleUpdateRule(tag.id, ruleIndex, {
                                      logic: e.target.value as TagRule['logic'],
                                    })
                                  }
                                  className="rounded border border-gray-300 bg-white px-2 py-1"
                                >
                                  <option value="AND">All conditions</option>
                                  <option value="OR">Any condition</option>
                                </select>
                              </label>
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                Priority
                                <input
                                  type="number"
                                  value={rule.priority ?? 0}
                                  onChange={e =>
                                    handleUpdateRule(tag.id, ruleIndex, {
                                      priority: Number(e.target.value),
                                    })
                                  }
                                  className="w-20 rounded border border-gray-300 bg-white px-2 py-1"
                                />
                              </label>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteRule(tag.id, ruleIndex)
                              }
                              className="cursor-pointer text-sm font-medium text-red-600 hover:underline"
                            >
                              Remove rule
                            </button>
                          </div>
                          <div className="space-y-2">
                            {rule.conditions.map(
                              (condition, conditionIndex) => {
                                const updateCondition = (
                                  updates: Partial<TagCondition>,
                                ) =>
                                  handleUpdateCondition(
                                    tag.id,
                                    ruleIndex,
                                    conditionIndex,
                                    updates,
                                  );

                                return (
                                  <div
                                    key={`${tag.id}-rule-${ruleIndex}-condition-${conditionIndex}`}
                                    className="rounded border border-gray-200 bg-white p-3"
                                  >
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                      <select
                                        value={condition.type}
                                        onChange={e => {
                                          const type = e.target
                                            .value as TagCondition['type'];
                                          handleDeleteCondition(
                                            tag.id,
                                            ruleIndex,
                                            conditionIndex,
                                          );
                                          setEditedTags(prev =>
                                            prev.map(currentTag =>
                                              currentTag.id === tag.id
                                                ? {
                                                    ...currentTag,
                                                    rules: (
                                                      currentTag.rules || []
                                                    ).map(
                                                      (currentRule, index) =>
                                                        index === ruleIndex
                                                          ? {
                                                              ...currentRule,
                                                              conditions: [
                                                                ...currentRule.conditions.slice(
                                                                  0,
                                                                  conditionIndex,
                                                                ),
                                                                createCondition(
                                                                  type,
                                                                ),
                                                                ...currentRule.conditions.slice(
                                                                  conditionIndex,
                                                                ),
                                                              ],
                                                            }
                                                          : currentRule,
                                                    ),
                                                  }
                                                : currentTag,
                                            ),
                                          );
                                        }}
                                        className="rounded border border-gray-300 px-2 py-1 text-sm"
                                      >
                                        <option value="description">
                                          Description
                                        </option>
                                        <option value="frequency">
                                          Frequency
                                        </option>
                                        <option value="callSign">
                                          Call sign
                                        </option>
                                      </select>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleDeleteCondition(
                                            tag.id,
                                            ruleIndex,
                                            conditionIndex,
                                          )
                                        }
                                        className="cursor-pointer text-sm text-red-600 hover:underline"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                    {condition.type === 'callSign' ? (
                                      <div className="grid gap-3 md:grid-cols-2">
                                        {(['from', 'to'] as const).map(
                                          field => {
                                            const operatorField =
                                              field === 'from'
                                                ? 'fromOperator'
                                                : 'toOperator';
                                            const caseSensitiveField =
                                              field === 'from'
                                                ? 'fromCaseSensitive'
                                                : 'toCaseSensitive';
                                            return (
                                              <div
                                                key={field}
                                                className="space-y-2"
                                              >
                                                <label className="block text-sm font-medium text-gray-700">
                                                  {field === 'from'
                                                    ? 'From'
                                                    : 'To'}
                                                  <input
                                                    type="text"
                                                    value={condition[field]}
                                                    onChange={e =>
                                                      updateCondition({
                                                        [field]: e.target.value,
                                                      })
                                                    }
                                                    className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5"
                                                  />
                                                </label>
                                                <select
                                                  value={
                                                    condition[operatorField]
                                                  }
                                                  onChange={e =>
                                                    updateCondition({
                                                      [operatorField]: e.target
                                                        .value as StringOperator,
                                                    })
                                                  }
                                                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                                >
                                                  {stringOperators.map(
                                                    ([value, label]) => (
                                                      <option
                                                        key={value}
                                                        value={value}
                                                      >
                                                        {label}
                                                      </option>
                                                    ),
                                                  )}
                                                </select>
                                                <label className="flex items-center gap-2 text-sm text-gray-600">
                                                  <input
                                                    type="checkbox"
                                                    checked={
                                                      condition[
                                                        caseSensitiveField
                                                      ]
                                                    }
                                                    onChange={e =>
                                                      updateCondition({
                                                        [caseSensitiveField]:
                                                          e.target.checked,
                                                      })
                                                    }
                                                  />
                                                  Case sensitive
                                                </label>
                                              </div>
                                            );
                                          },
                                        )}
                                      </div>
                                    ) : (
                                      <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                                        <input
                                          type="text"
                                          value={condition.value}
                                          onChange={e =>
                                            updateCondition({
                                              value: e.target.value,
                                            })
                                          }
                                          placeholder={`Value to match in ${condition.type}`}
                                          className="rounded border border-gray-300 px-2 py-1.5"
                                        />
                                        <select
                                          value={condition.operator}
                                          onChange={e =>
                                            updateCondition({
                                              operator: e.target
                                                .value as StringOperator,
                                            })
                                          }
                                          className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                                        >
                                          {stringOperators.map(
                                            ([value, label]) => (
                                              <option key={value} value={value}>
                                                {label}
                                              </option>
                                            ),
                                          )}
                                        </select>
                                        <label className="flex items-center gap-2 text-sm text-gray-600 md:col-span-2">
                                          <input
                                            type="checkbox"
                                            checked={condition.caseSensitive}
                                            onChange={e =>
                                              updateCondition({
                                                caseSensitive: e.target.checked,
                                              })
                                            }
                                          />
                                          Case sensitive
                                        </label>
                                      </div>
                                    )}
                                  </div>
                                );
                              },
                            )}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {(
                              ['description', 'frequency', 'callSign'] as const
                            ).map(type => (
                              <button
                                key={type}
                                type="button"
                                onClick={() =>
                                  handleAddCondition(tag.id, ruleIndex, type)
                                }
                                className="cursor-pointer rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                              >
                                +{' '}
                                {
                                  {
                                    description: 'Description',
                                    frequency: 'Frequency',
                                    callSign: 'Call Sign',
                                  }[type]
                                }
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add New Tag */}
          <div className="mt-6 border-t border-gray-300 pt-6">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              Add New Tag
            </h3>
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="new-tag-name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Tag Name
                </label>
                <input
                  id="new-tag-name"
                  type="text"
                  value={newTagName}
                  onChange={e => setNewTagName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="e.g., Urgent, Review, Completed"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="new-tag-color"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="new-tag-color"
                      type="color"
                      value={newTagColor}
                      onChange={e => setNewTagColor(e.target.value)}
                      className="h-10 w-12 cursor-pointer rounded border border-gray-300 p-0"
                    />
                    <input
                      type="text"
                      value={newTagColor}
                      onChange={e => setNewTagColor(e.target.value)}
                      className="flex-1 rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
              <button
                onClick={handleAddTag}
                disabled={!newTagName.trim()}
                className={clsx(
                  'rounded-md px-4 py-2 font-medium',
                  newTagName.trim()
                    ? 'cursor-pointer bg-green-600 text-white hover:bg-green-700'
                    : 'cursor-not-allowed bg-gray-300 text-gray-500',
                )}
              >
                Add Tag
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={closeDialog}
            className="cursor-pointer rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
          >
            Save Tags
          </button>
        </div>
      </div>
    </div>
  );
}
